import React from 'react';
import { ProductDetailsCard } from '../organisms/ProductDetailsCard';
import { EnrollmentCardContainerComp } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainerComp';
import type { SlotContent } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

import {
  SlotTrackingContext,
  normalizeProductDetailsCardProps,
  normalizeEnrollmentCardProps,
  normalizeLayeredFeatureCardProps,
  normalizeMerchandisingCoBrandCardProps,
  normalizeRiottMediaCardProps,
} from './slotPropNormalizers';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  MerchandisingLayeredFeature,
  MerchandisingCoBrandCard,
  RiottMediaCardNonEditable,
} from '@marriott/mi-merch-components';

const CARD_TYPE_MAP = {
  layeredfeaturecard: 'layeredfeaturecard',
  productdetailscard: 'productdetailscard',
  enrollmentcard: 'enrollmentcard',
  cobrandcard: 'cobrandcard',
  riottmediacard: 'riottmediacard',
} as const;

type CardTypeKey = (typeof CARD_TYPE_MAP)[keyof typeof CARD_TYPE_MAP];

function isCardTypeKey(s: string): s is keyof typeof CARD_TYPE_MAP {
  return s in CARD_TYPE_MAP;
}

function normalizeCardType(input: string): CardTypeKey | null {
  const normalized = input
    .trim()
    .toLowerCase()
    .replace(/model$/, '');
  return isCardTypeKey(normalized) ? CARD_TYPE_MAP[normalized] : null;
}

function getDcaCardType(metaData: Record<string, unknown> | undefined): CardTypeKey {
  if (!metaData || typeof metaData !== 'object') return CARD_TYPE_MAP.productdetailscard;
  if (metaData['isMango'] === true) return CARD_TYPE_MAP.riottmediacard;
  if ((metaData['type'] as string)?.toLowerCase() === 'cobrand') return CARD_TYPE_MAP.cobrandcard;
  if ((metaData['type'] as string)?.toLowerCase() === 'non-cobrand') return CARD_TYPE_MAP.productdetailscard;
  return CARD_TYPE_MAP.productdetailscard;
}

export function resolveSlotComponent(slot: SlotContent, trackingCtx?: SlotTrackingContext): React.ReactElement | null {
  const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';

  const usingCfFallback =
    slot?.type === 'dca' && (slot?.payload === null || slot?.payload === undefined) && slot?.cfFallback != null;
  const effectivePayload = usingCfFallback ? slot.cfFallback : slot?.payload;
  const effectiveSlot: SlotContent = { ...slot, payload: effectivePayload };

  const resolvedCardType =
    size === 'banner'
      ? CARD_TYPE_MAP.layeredfeaturecard
      : slot?.type === 'cf' || usingCfFallback
      ? normalizeCardType(String((effectivePayload as Record<string, unknown>)?.['cardType'] ?? '')) ||
        CARD_TYPE_MAP.productdetailscard
      : getDcaCardType(
          ((effectivePayload as Record<string, unknown>)?.['metaData'] ??
            (effectivePayload as Record<string, unknown>)?.['metadata']) as Record<string, unknown> | undefined
        );

  const ctx = { slot: effectiveSlot, trackingCtx };

  switch (resolvedCardType) {
    case CARD_TYPE_MAP.layeredfeaturecard:
      return <MerchandisingLayeredFeature {...normalizeLayeredFeatureCardProps(ctx)} />;
    case CARD_TYPE_MAP.productdetailscard:
      return <ProductDetailsCard {...normalizeProductDetailsCardProps(ctx)} />;
    case CARD_TYPE_MAP.enrollmentcard:
      return <EnrollmentCardContainerComp {...normalizeEnrollmentCardProps(ctx)} />;
    case CARD_TYPE_MAP.cobrandcard:
      return <MerchandisingCoBrandCard {...(normalizeMerchandisingCoBrandCardProps(ctx) as object)} />;
    case CARD_TYPE_MAP.riottmediacard:
      return <RiottMediaCardNonEditable {...normalizeRiottMediaCardProps(ctx)} />;
    default:
      return <ProductDetailsCard {...normalizeProductDetailsCardProps(ctx)} />;
  }
}
